import React, { useRef } from "react";
import { DeepMap, FieldError, useFormContext, useWatch } from "react-hook-form";
import { Form, Header, Grid, GridColumn, FormField, Message } from "semantic-ui-react";
import { DateValue } from "react-aria-components";

import { ControlledTextArea, IStrictControlledTextAreaProps } from "../../../../../components/ControlledForm";
import { ControlledDateTime } from "../../../../../components/global_components/form_components/ControlledDateTime";
import { IInspectionRequestForm } from "./InspectionRequestModalContent";
import ControlledChipRadioSelect from "./ControlledChipRadioSelect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMessage } from "@fortawesome/free-regular-svg-icons";
import usePublicInspection from "../../../../tanstack/inspections/public/usePublicInspection";

type BlockOutDayDoW = BlockOutDay["dayOfWeek"];
type BlockOutDay = {
  key: string;
  dayOfWeek: "MONDAY" | "TUESDAY" | "WEDNESDAY" | "THURSDAY" | "FRIDAY" | "SATURDAY" | "SUNDAY";
  meridiem: "AM" | "PM" | null;
};
interface IInspectionRequestDateTimeForm {
  id: string;
  errors: DeepMap<IInspectionRequestForm, FieldError>;
}
export default function InspectionRequestDateTimeForm({ id, errors }: IInspectionRequestDateTimeForm) {
  const { control } = useFormContext();
  const { data } = usePublicInspection({ id });
  const ref = useRef<HTMLDivElement | null>(null);
  const helpText = data?.data.attributes.citizenHelpText ?? "";
  const blockOutDays = data?.data.attributes.blockOutDays ?? [];

  const inspectionRequestPreferences: string[] = ["First Preference", "Second Preference", "Third Preference"];
  const preferences = useWatch<{ date: Date }[] | undefined>({ name: "preferences" });

  return (
    <div ref={ref}>
      <Form>
        <Header as='h4' className='tw-mb-0'>
          Date and Time
        </Header>
        <p className='subText tw-my-0'>{helpText}</p>
        <InspectionRejectedMessage id={id} />
        {inspectionRequestPreferences.map((requestTimeSlot, index) => {
          const options = getMeridiemOptions(blockOutDays, preferences?.[index]?.date);
          return (
            <>
              <Grid className='tw-mt-0 tw-py-2' stackable>
                <GridColumn className='tw-py-0' width={8}>
                  <FormField required error={!!errors.preferences?.[index]}>
                    <ControlledDateTime
                      label={requestTimeSlot}
                      required
                      control={control}
                      name={`preferences[${index}].date`}
                      granularity='day'
                      errors={errors}
                      isDateUnavailable={isDateUnAvailable(blockOutDays)}
                      rules={{
                        validate: {
                          _date: (value) => {
                            if (!(value instanceof Date)) {
                              return "Preference must be a valid date";
                            }
                            return true;
                          },
                        },
                      }}
                      UNSTABLE_portalContainer={ref.current ?? undefined}
                    />
                  </FormField>
                </GridColumn>
                <GridColumn className='tw-py-0' width={8}>
                  <ControlledChipRadioSelect
                    control={control}
                    errors={errors}
                    name={`preferences[${index}].meridiem`}
                    options={options}
                    rules={{
                      validate: {
                        _nonEmpty: (value) => {
                          if (value === undefined) {
                            return "Please select a time block";
                          }
                          return true;
                        },
                      },
                    }}
                  />
                </GridColumn>
              </Grid>
            </>
          );
        })}
        <FormField className='tw-mt-2' error={!!errors.message}>
          <ControlledTextArea<IStrictControlledTextAreaProps>
            control={control}
            errors={errors}
            label={"Add a Message About Your Scheduling Preferences"}
            name={"message"}
            rows={2}
            placeholder='Before 10 AM would be best, I can do anytime other than 3-4 PM, etc'
            rules={{
              validate: {
                _length: (value) => {
                  if (typeof value !== "string") {
                    return true;
                  }
                  const MAX_LENGTH = 191;
                  if (value.length > MAX_LENGTH) {
                    return `Name cannot be greater than ${MAX_LENGTH} characters`;
                  }
                  return true;
                },
              },
            }}
          />
        </FormField>
      </Form>
    </div>
  );
}

function InspectionRejectedMessage({ id }: { id: string }) {
  const { data } = usePublicInspection({ id });

  const rejectedDate = data?.data.attributes.request.rejectedAt ?? null;
  const rejectedMessage = data?.data.attributes.request.rejectionMessage ?? null;

  return <RejectedMessage message={rejectedMessage} date={rejectedDate} />;
}

interface IRejectedMessage {
  message: string | null;
  date: string | null;
}
function RejectedMessage({ message, date }: IRejectedMessage) {
  if (date === null) {
    return <></>;
  }

  return (
    <Message className='tw-mb-0' negative>
      <FontAwesomeIcon className='tw-mr-2' icon={faMessage} />
      {message}
    </Message>
  );
}

const BlockOutDayDoWToJSDateDoW: Record<BlockOutDayDoW, number> = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
} as const;

function getMeridiemOptions(
  blockOutDays: BlockOutDay[],
  preference: Date | null | undefined,
): { value: string | null; text: string }[] {
  if (isAMBlockedOut(blockOutDays, preference)) {
    return [{ value: "PM", text: "Only PM available" }];
  }
  if (isPMBlockedOut(blockOutDays, preference)) {
    return [{ value: "AM", text: "Only AM available" }];
  }

  return [
    { value: "AM", text: "AM" },
    { value: "PM", text: "PM" },
    { value: null, text: "Anytime" },
  ];
}

function isAMBlockedOut(blockOutDays: BlockOutDay[], preference: Date | null | undefined): boolean {
  if (!preference) {
    return false;
  }
  const morningBlockOutDays = blockOutDays.filter((day) => day.meridiem !== "PM");
  return morningBlockOutDays.some((day) => {
    return BlockOutDayDoWToJSDateDoW[day.dayOfWeek] === preference.getDay();
  });
}

function isPMBlockedOut(blockOutDays: BlockOutDay[], preference: Date | null | undefined): boolean {
  if (!preference) {
    return false;
  }
  const afternoonBlockOutDays = blockOutDays.filter((day) => day.meridiem !== "AM");
  return afternoonBlockOutDays.some((day) => {
    return BlockOutDayDoWToJSDateDoW[day.dayOfWeek] === preference.getDay();
  });
}

function isDateUnAvailable(blockOutDays: BlockOutDay[]): (date: DateValue) => boolean {
  function isDayBlockedOut(date: Date): boolean {
    const blockedOutDays = blockOutDays
      .filter((blockOutDay) => {
        return blockOutDay.meridiem === null;
      })
      .map((blockOutDay) => blockOutDay.dayOfWeek);

    return blockedOutDays.some((day) => {
      return BlockOutDayDoWToJSDateDoW[day] === date.getDay();
    });
  }

  return (date: DateValue): boolean => {
    const tz = new window.Intl.DateTimeFormat().resolvedOptions().timeZone;
    const dateObject = date.toDate(tz);
    return isDayBlockedOut(dateObject);
  };
}
